import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, fmt, fmtDate } from '../api';
import { Loading, Empty, ErrorBox, Eyebrow } from '../ui';

/**
 * «Shartnoma bera olamiz» — qancha summalik shartnoma taklif qila olamiz.
 * Ikkita mustaqil hisob bir jadvalda:
 *   1) Shartnoma narxi = eng yuqori prixod × 1,1 (ustama) × 1,12 (NDS)
 *   2) Kerakli prixod  = sotuv narxi × 0,6  — prixodni qanchaga ko'tarish kerakligi
 * Natija kategoriyalar bo'yicha bo'lim-bo'lim (1C hisobotidagi kabi) ko'rsatiladi.
 */

// Kategoriya ustuni yo'q: qatorlar allaqachon kategoriya bo'limlariga ajratilgan,
// uni har qatorda takrorlash faqat joy egallaydi.
const COLS = 9;

type CatNode = {
  key: string; label: string; code: string | null;
  sortOrder: number; depth: number;
  children: CatNode[]; rows: any[];
};

/** Qatorlardagi kategoriya yo'llaridan bo'limlar daraxtini yig'adi. */
function buildTree(rows: any[]): CatNode[] {
  const root: CatNode[] = [];
  const index = new Map<string, CatNode>();
  const ensure = (list: CatNode[], key: string, label: string, code: string | null, sortOrder: number, depth: number) => {
    let n = index.get(key);
    if (!n) {
      n = { key, label, code, sortOrder, depth, children: [], rows: [] };
      index.set(key, n);
      list.push(n);
    }
    return n;
  };

  for (const r of rows) {
    const path: any[] = r.categoryPath || [];
    if (!path.length) {
      // ⚠ Kategoriyasi belgilanmagan mahsulot ham ko'rinishi shart, aks holda
      //   jadvaldagi jami son bilan bo'limlardagi son mos kelmay qoladi.
      ensure(root, '__none__', 'Kategoriyasiz', null, Number.MAX_SAFE_INTEGER, 0).rows.push(r);
      continue;
    }
    let list = root;
    let key = '';
    let node: CatNode | undefined;
    for (let i = 0; i < path.length; i++) {
      const c = path[i];
      key = key ? key + '/' + c.id : String(c.id);
      node = ensure(list, key, c.name, c.code ?? null, c.sortOrder ?? 0, i);
      list = node.children;
    }
    if (node) node.rows.push(r);
  }

  const sortRec = (nodes: CatNode[]) => {
    nodes.sort((a, b) =>
      a.sortOrder - b.sortOrder ||
      String(a.code ?? '').localeCompare(String(b.code ?? ''), undefined, { numeric: true }) ||
      a.label.localeCompare(b.label));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(root);
  return root;
}

/** Bo'lim sarlavhasidagi sonlar — ostki bo'limlar bilan birga. */
function countRec(n: CatNode): { total: number; short: number } {
  let total = n.rows.length;
  let short = n.rows.filter((r) => r.gap != null && r.gap > 0).length;
  for (const c of n.children) {
    const s = countRec(c);
    total += s.total; short += s.short;
  }
  return { total, short };
}

function allKeys(nodes: CatNode[], out = new Set<string>()) {
  for (const n of nodes) { out.add(n.key); allKeys(n.children, out); }
  return out;
}

export default function Contract() {
  const [d, setD] = useState<any>(null);
  const [err, setErr] = useState('');
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('');
  const [category, setCategory] = useState('');
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [dl, setDl] = useState(false);

  /** Amaldagi filtrlar — ro'yxat va Excel eksporti bir xil so'rovni ishlatsin. */
  const qs = () => {
    const p = new URLSearchParams();
    if (search) p.set('search', search);
    if (brand) p.set('brand', brand);
    if (category) p.set('category', category);
    return p.toString() ? '?' + p.toString() : '';
  };

  const load = () => {
    setD(null); setErr('');
    api.get('/contract-capacity' + qs()).then(setD).catch((e) => setErr(e.message));
  };
  useEffect(() => { load(); }, [brand, category]);

  const tree = useMemo(() => buildTree(d?.rows || []), [d]);
  // Yangi ma'lumot kelganda barcha bo'limlar ochiq — hech narsa yashirin qolmasin
  useEffect(() => { setOpen(allKeys(tree)); }, [tree]);

  const toggle = (k: string) => setOpen((prev) => {
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const k60 = d?.k60 ?? 0.6;
  const brands: string[] = d?.filters?.brands || [];
  const categories: string[] = d?.filters?.categories || [];
  const allOpen = d && open.size >= allKeys(tree).size;

  const productRow = (r: any, depth: number) => (
    <tr key={r.productId} className="prod-row">
      <td className="c-art" style={{ paddingLeft: 15 + (depth + 1) * 20 }}>
        <Link to={'/products/' + r.productId} className="art-link">{r.article}</Link>
      </td>
      <td className="c-name">{r.name}</td>
      <td className="c-brand">{r.brand || '—'}</td>

      {/* ── Prixod narxi ── */}
      <td className="num c-max grp-start">{fmt(r.maxPrixod, 2)}</td>
      <td className="num c-hist">
        {r.last3?.length ? (
          <div className="hist">
            {r.last3.map((h: any, i: number) => (
              <span
                key={i}
                className={'hist-i' + (h.value === r.maxPrixod ? ' is-max' : '')}
                title={(h.from ? 'Yuk ' + h.from + ' · ' : '') + fmtDate(h.date)}
              >
                <b>{fmt(h.value, 2)}</b>
                <i>{fmtDate(h.date).slice(0, 5)}</i>
              </span>
            ))}
            {r.prixodCount > 3 && <span className="hist-more">+{r.prixodCount - 3}</span>}
          </div>
        ) : <span className="muted">—</span>}
      </td>

      {/* ── Shartnoma narximiz ── */}
      <td className="num c-contract grp-start">{fmt(r.contractPrice, 2)}</td>

      {/* ── Sotuv narxidan kelib chiqib ── */}
      <td className="num c-sale grp-start">
        {r.saleUzs != null ? (
          <>
            <div>{fmt(r.saleUzs)}</div>
            {r.salePrice != null && r.saleCurrency !== 'UZS' && (
              <div className="sub">{fmt(r.salePrice, 2)} {r.saleCurrency}</div>
            )}
          </>
        ) : <span className="muted">—</span>}
      </td>
      <td className="num c-need">
        {r.requiredPrixod != null ? fmt(r.requiredPrixod, 2) : <span className="muted">—</span>}
      </td>
      <td className="num c-gap">
        {r.gap == null ? <span className="muted">—</span> : r.gap > 0 ? (
          <span className="gap gap-up" title="Shartnoma narximiz kerakli darajadan shuncha past">
            +{fmt(r.gap, 2)}
            {r.gapPct != null && <i>+{fmt(r.gapPct, 0)}%</i>}
          </span>
        ) : (
          <span className="gap gap-ok" title="Shartnoma narximiz kerakli darajaga yetadi">✓ {fmt(Math.abs(r.gap), 2)}</span>
        )}
      </td>
    </tr>
  );

  const renderNode = (n: CatNode): any[] => {
    const c = countRec(n);
    const isOpen = open.has(n.key);
    // Ildiz bo'limi ko'zga tashlanib tursin, ostkilari yengilroq
    const lvl = n.depth === 0 ? ' lvl0' : ' lvl1';
    const out: any[] = [
      <tr key={'g:' + n.key} className={'cat-row' + lvl + (isOpen ? '' : ' is-closed')}
          onClick={() => toggle(n.key)}>
        <td colSpan={COLS}>
          <div className="cat-inner" style={{ paddingLeft: n.depth * 20 }}>
            <span className="cat-caret">{isOpen ? '▾' : '▸'}</span>
            {n.code && <span className="cat-code">{n.code}</span>}
            <span className="cat-name">{n.label}</span>
            <span className="cat-spacer" />
            {c.short > 0 && (
              <span className="cat-badge is-short">{c.short} ta yetmaydi</span>
            )}
            <span className="cat-badge">{c.total} ta</span>
          </div>
        </td>
      </tr>,
    ];
    if (!isOpen) return out;
    for (const child of n.children) out.push(...renderNode(child));
    for (const r of n.rows) out.push(productRow(r, n.depth));
    return out;
  };

  return (
    <div>
      <div className="page-head">
        <div>
          <h1 className="page-title">Shartnoma bera olamiz</h1>
          <p className="page-sub">
            Har mahsulotning <b>eng yuqori prixod narxi</b> asosida: shartnoma narxi = prixod × 1,1 (ustama) × 1,12 (NDS).
            <br />
            <b>Kerakli prixod</b> = sotuv narxi × {fmt(k60, 2)} — prixodni sotuv narxini oqlaydigan darajaga
            ko'tarish uchun qancha bo'lishi kerakligi.
          </p>
        </div>
      </div>

      <form className="filters" onSubmit={(e) => { e.preventDefault(); load(); }}>
        <input className="search" placeholder="Artikul / nom bo'yicha qidirish" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="input" style={{ width: 160 }} value={brand} onChange={(e) => setBrand(e.target.value)}>
          <option value="">Barcha brendlar</option>
          {brands.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select className="input" style={{ width: 210 }} value={category} onChange={(e) => setCategory(e.target.value)}>
          <option value="">Barcha kategoriyalar</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <button className="btn">Qidirish</button>
        {(search || brand || category) && (
          <button type="button" className="btn btn-ghost" onClick={() => { setSearch(''); setBrand(''); setCategory(''); }}>Tozalash</button>
        )}
      </form>

      {err ? <ErrorBox text={err} onRetry={load} /> : !d ? <Loading /> : !d.rows.length ? (
        // Filtr qo'yilganda «narx kiritilmagan» deyish chalg'itardi — sabab boshqa
        (search || brand || category) ? (
          <Empty text="Filtrga mos mahsulot topilmadi" hint="Qidiruv so'zini yoki tanlangan brend / kategoriyani o'zgartirib ko'ring" />
        ) : (
          <Empty
            text="Hali prixod narxi kiritilmagan"
            hint="Rastamojka hujjatida «Prixod narxi (so'm)» ustunini to'ldiring va hujjatni tasdiqlang — shundan keyin bu yerda hisob chiqadi"
          />
        )
      ) : (
        <>
          <div className="stat-grid">
            <div className="stat">
              <div className="k">Mahsulot turi</div>
              <div className="v">{fmt(d.totals.products)}</div>
              <div className="d">prixod narxi mavjudlari</div>
            </div>
            <div className="stat ok">
              <div className="k">Koeffitsiyent</div>
              <div className="v">×{(d.markup * d.vat).toFixed(3)}</div>
              <div className="d">1,1 ustama × 1,12 NDS</div>
            </div>
            <div className={'stat ' + (d.totals.short > 0 ? 'danger' : 'ok')}>
              <div className="k">Sotuvga yetmaydiganlar</div>
              <div className="v">{fmt(d.totals.short)} ta</div>
              <div className="d">{fmt(d.totals.withSale)} tasida sotuv narxi bor</div>
            </div>
          </div>

          {!d.uzsRate && (
            <ErrorBox text="UZS kursi sozlanmagan — sotuv narxini so'mga o'girib bo'lmadi. Valyutalar bo'limidan kursni kiriting." />
          )}

          <div className="tbl-bar">
            <Eyebrow title="Kategoriyalar bo'yicha" desc="bo'lim ichida shartnoma narxi bo'yicha kamayish tartibida" />
            <div className="tbl-actions">
              <button
                type="button"
                className="btn btn-ghost btn-sm"
                onClick={() => setOpen(allOpen ? new Set() : allKeys(tree))}
              >
                {allOpen ? '⊟ Bo\'limlarni yig\'ish' : '⊞ Bo\'limlarni yoyish'}
              </button>
              {/* Oddiy havola ishlamaydi — endpoint avtorizatsiya talab qiladi,
                  shuning uchun token bilan yuklab, blob orqali saqlanadi */}
              <button
                type="button"
                className="btn btn-sm"
                disabled={dl}
                onClick={() => {
                  setDl(true);
                  api.download('/contract-capacity/export' + qs(), 'shartnoma-bera-olamiz.xlsx')
                    .catch((e) => setErr(e.message))
                    .finally(() => setDl(false));
                }}
              >
                {dl ? 'Tayyorlanmoqda…' : '⤓ Excel'}
              </button>
            </div>
          </div>

          <div className="table-wrap contract-tbl">
            <table>
              <thead>
                {/* Ustunlar ma'no jihatidan uch guruhga bo'linadi — busiz 9 ta
                    ustun bir xil ko'rinib, qaysi biri nimaga tegishli chalkashardi */}
                <tr className="grp-head">
                  <th colSpan={3} className="grp-none">Mahsulot</th>
                  <th colSpan={2} className="grp-a">Prixod narxi</th>
                  <th colSpan={1} className="grp-b">Shartnoma</th>
                  <th colSpan={3} className="grp-c">Sotuv narxidan kelib chiqib</th>
                </tr>
                <tr>
                  <th>Artikul</th>
                  <th>Nomi</th>
                  <th>Brend</th>
                  <th className="num grp-start">Eng yuqori</th>
                  <th className="num">Oxirgi 3 kirim</th>
                  <th className="num grp-start">Bera olamiz<span className="th-sub">prixod × {(d.markup * d.vat).toFixed(3)}</span></th>
                  <th className="num grp-start">Sotuv narxi<span className="th-sub">so'mda</span></th>
                  <th className="num">Kerakli prixod<span className="th-sub">sotuv × {fmt(k60, 2)}</span></th>
                  <th className="num">Farq<span className="th-sub">kerakli − bera olamiz</span></th>
                </tr>
              </thead>
              <tbody>
                {tree.flatMap((n) => renderNode(n))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
